import React, { useState, useRef, useEffect } from "react";
import { Grid } from "@mui/material";
import FlipCameraIosIcon from "@mui/icons-material/FlipCameraIos";
import ReactDOM from "react-dom";

import style from "./index.module.css";

import GetImages from "@/components/GetImages";

import { storage } from "@/utils/Storage";
import { notify } from "@/utils/toaster";
import { INotifyEnum } from "@/utils/toaster";

import { IRoleType } from "@/models/role";
import { ISessionKeys } from "@/models";

import { useAppDispatch } from "@/store/hooks";
import {
  setIsCameraOn,
  setIsCreateCameraPrescription,
  setIsPrescriptionCaptured,
} from "@/store/slices/doctor/prescription";
import { setMedicalReport } from "@/store/slices/doctor/medicalRecord";

const Camera: React.FC = () => {
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const dispatch = useAppDispatch();

  const roleType = storage.getSessionToken(ISessionKeys.ROLE_TYPE);

  const startCamera = async () => {
    try {
      // Check if we're in WKWebView on iOS
      const isWKWebView =
        /iPad|iPhone|iPod/.test(navigator.userAgent) &&
        !(window as any).MSStream &&
        (window as any).webkit?.messageHandlers;

      if (isWKWebView) {
        // For WKWebView, we need to ensure we're on iOS 13+
        const iosVersion = parseInt(
          navigator.userAgent.match(/OS (\d+)_/)?.[1] || "0"
        );
        if (iosVersion < 13) {
          notify(
            INotifyEnum.ERROR,
            "Please upgrade to iOS 13 or later to use camera features"
          );
          return;
        }
      }

      const mediaStream = await navigator.mediaDevices?.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      if (!mediaStream) {
        throw new Error("Failed to get media stream");
      }

      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play().catch((err) => {
          console.error("Error playing video:", err);
          notify(INotifyEnum.ERROR, "Error starting camera. Please try again.");
        });
      }
    } catch (err) {
      console.error("Error accessing the camera:", err);
      let errorMessage = "Error accessing the camera.";

      if (
        err.name === "NotAllowedError" ||
        err.name === "PermissionDeniedError"
      ) {
        errorMessage =
          "Camera permission was denied. Please enable camera access in your browser settings.";
      } else if (
        err.name === "NotFoundError" ||
        err.name === "DevicesNotFoundError"
      ) {
        errorMessage = "No camera found on your device.";
      } else if (
        err.name === "NotReadableError" ||
        err.name === "TrackStartError"
      ) {
        errorMessage = "Your camera is already in use by another application.";
      }

      notify(INotifyEnum.ERROR, errorMessage);
    }
  };

  const switchCamera = () => {
    setFacingMode((prevMode) => (prevMode === "user" ? "environment" : "user"));
  };

  const captureImage = () => {
    if (canvasRef.current && videoRef.current) {
      const context = canvasRef.current.getContext("2d");
      if (context) {
        context.drawImage(
          videoRef.current,
          0,
          0,
          canvasRef.current.width,
          canvasRef.current.height
        );
        const dataUrl = canvasRef.current.toDataURL("image/png");
        setCapturedImage(dataUrl);
      }
    }
  };

  const backHandler = () => {
    stopCamera();
    dispatch(setIsCameraOn(false));
    dispatch(setIsCreateCameraPrescription(false));
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => {
        track.stop();
      });
      setStream(null);
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  };

  useEffect(() => {
    startCamera();
  }, [facingMode]);

  useEffect(() => {
    if (capturedImage && capturedImage.length > 0) {
      if (roleType === IRoleType.DOCTOR || roleType === IRoleType.JR_DOCTOR) {
        dispatch(
          setIsPrescriptionCaptured({
            capturedImage,
            isCapturedSave: false,
          })
        );
        dispatch(setIsCameraOn(false));
        dispatch(setIsCreateCameraPrescription(true));
      } else {
        dispatch(
          setMedicalReport({
            identifier: "isCapturedSave",
            value: true,
          })
        );
        dispatch(
          setMedicalReport({
            identifier: "image",
            value: capturedImage,
          })
        );
        dispatch(setIsCameraOn(false));
      }
    }
    stopCamera();
  }, [capturedImage]);

  return ReactDOM.createPortal(
    <Grid item className={style.container}>
      <Grid item className={style.arrowBackBtn} onClick={backHandler}>
        <GetImages name="ArrowLeftWhiteIcon" width="16" height="16" />
      </Grid>
      <Grid item className={style.cameraView}>
        <video ref={videoRef} className={style.videoFeed}></video>
        <canvas
          ref={canvasRef}
          width="328"
          height="584"
          className={style.hidden}
        ></canvas>
      </Grid>
      <Grid item className={style.imageCaptureContainer} onClick={captureImage}>
        <GetImages name="CameraCaptureIcon" width="44" height="44" />
      </Grid>
      <Grid item className={style.switchCamera}>
        <FlipCameraIosIcon
          sx={{ fontSize: "4.4rem", color: "var(--orange-500)" }}
          onClick={switchCamera}
        />
      </Grid>

      <Grid item className={style.bottomLine}></Grid>
    </Grid>,
    document.getElementById("portal-root") as HTMLElement
  );
};

export default Camera;
